import React from "react";
import * as ReactDOM from "react-dom";
import { Icon, EmptyState, computeQuoteView, quotePayments, quoteDeposit, useLocalPref, useNarrowScreen } from "./registry";
import { NumInput, Seg } from "./business/fields";
import { PlanChart } from "./business/planchart";
import { CashSchedules, QboStrip } from "./business/cashschedules";
import {
  TYPICAL_SEASON, DEFAULT_SPLIT, normalizeSeason, setSeasonMonth, revenueByMonth, seasonFromHistory, planBuckets, monthlyPlan,
  crewMonthHours, revPerCrewHour, commissionRate, funnelStats, workBackwards, quarterlyPlan, allocateProfit,
} from "./lib/planner";
import { ymdOf, addDaysYmd, mondayOf, monthlyEquivalent, scheduleMoves, buildForecast, cashKpis } from "./lib/cashplan";
/* ============================================================
   Fence Works — Budget & Cash (the business side)
   Two owner tools, both computed live from the jobs already in the app
   (quotes, work orders, payments, crews, reps) plus a handful of inputs
   persisted in the settings blob (settings.budget / settings.cash):

   1. GOAL PLANNER — the four buckets (overhead $/mo; labor, commission
      and materials as % of revenue; target net %) → the revenue the year
      needs, spread month by month by the shop's own seasonality, checked
      against crew capacity, tracked against what's completed and
      scheduled, worked back to jobs / quotes / leads / marketing, and
      split into quarterly net and a profit allocation. Math: lib/planner.
   2. CASH FORECAST — 13 / 26 / 52 weeks: pipeline inflows (deposits,
      balances on completion, money already owed) and outflows (materials
      before install, labor on install week) plus the owner's bills,
      recurring costs, payroll and loan payments → a weekly radar with
      HEALTHY / NEAR FLOOR / BELOW FLOOR / PAYROLL WEEK. Math: lib/cashplan.
   ============================================================ */
const BZ_money0 = window.VFP.money0;
const DAY = 86400000;
const WON = ["signed", "paid"];
const signed0 = (n: number) => (n < 0 ? "−" : "") + BZ_money0(Math.abs(n));
const MONTH_LB = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function BucketTile({ label, hint, children, accent }: any) {
  return (
    <div className="card pad" style={{ padding: "14px 16px", borderColor: accent || "var(--line)" }}>
      <div className="kicker" style={{ marginBottom: 8 }} title={hint}>{label}</div>
      {children}
      {hint && <div className="faint" style={{ fontSize: 10.5, marginTop: 6, lineHeight: 1.4 }}>{hint}</div>}
    </div>
  );
}

/* the quote engine already knows this shop's real labor/material ratios —
   average the recent quotes so the buckets start from THEIR numbers, not ours */
function derivedRatios(store: any) {
  const qs = (store.quotes || []).slice(-20);
  const l: number[] = [], m: number[] = [];
  qs.forEach((q: any) => {
    try {
      const v = computeQuoteView(q, store.settings || {});
      const qq = v && v.quote;
      const sub = Number(qq && qq.subtotal) || 0;
      if (sub > 0) { l.push(((Number(qq.laborCost) || 0) / sub) * 100); m.push(((Number(qq.materialTotal) || 0) / sub) * 100); }
    } catch (e) { /* a malformed old quote never blocks the planner */ }
  });
  const avg = (a: number[]) => a.length ? a.reduce((x, y) => x + y, 0) / a.length : null;
  return { laborPct: avg(l), matPct: avg(m), n: l.length };
}

/* revenue per quoted crew-hour: won jobs of the last 12 months, else the last
   20 quotes. Units matter here. Capacity counts crew-hours the way the
   schedule's capacity strip and Settings → Crews do: people × hours. The
   engine's laborHours are hours the whole crew is ON SITE (it prices them at
   the crew's combined rate), so they're turned into the same unit — × the
   headcount of the crew the engine priced with (the active crew, else the
   first). Without that, a 3-person shop's capacity reads 3× too high. */
function crewHourRate(store: any) {
  const settings = store.settings || {};
  const quotes = store.quotes || [];
  const from = Date.now() - 365 * DAY;
  const won = quotes.filter((q: any) => WON.includes(q.status) && (Number(q.wonAt) || Number(q.createdAt) || 0) >= from);
  const pool = won.length ? won : quotes.slice(-20);
  const crews = settings.crews || [];
  const pc = crews.find((c: any) => c.id === settings.activeCrewId) || crews[0];
  const heads = Math.max(1, pc ? (Array.isArray(pc.members) && pc.members.length ? pc.members.length : (Number(pc.size) || 2)) : (Number(settings.laborCrewSize) || 2));
  const pairs: any[] = [];
  pool.forEach((q: any) => {
    try { pairs.push({ total: Number(q.total) || 0, hours: (Number(computeQuoteView(q, settings).quote.laborHours) || 0) * heads }); } catch (e) {}
  });
  return { rate: revPerCrewHour(pairs), n: pairs.filter((p) => p.total > 0 && p.hours > 0).length, won: won.length > 0 };
}

const STRESS = [
  { min: 1.15, label: "HEALTHY",  col: "var(--green)", bg: "var(--green-tint)" },
  { min: 0.85, label: "WATCH",    col: "var(--amber)", bg: "var(--amber-tint)" },
  { min: 0.5,  label: "STRESSED", col: "#f0a35e",      bg: "rgba(240,163,94,0.14)" },
  { min: -1,   label: "CRITICAL", col: "var(--red)",   bg: "var(--red-tint)" },
];
const stressOf = (ratio: number) => STRESS.find(s => ratio >= s.min) || STRESS[STRESS.length - 1];

/* a plan under three moods. Base is the goal as set; the other two are saved
   overrides (settings.budget.scenarios) — a revenue multiplier on the plan and
   a win-rate shift for the work-backwards funnel. */
const SCENARIOS = [
  { id: "base", label: "Base", revMult: 1, winDelta: 0 },
  { id: "conservative", label: "Conservative", revMult: 0.85, winDelta: -5 },
  { id: "aggressive", label: "Aggressive", revMult: 1.15, winDelta: 5 },
];
/* what the funnel assumes when the shop's own records are too thin */
const FUNNEL_DEFAULTS: any = { avgJob: 6500, winRate: 30, leadToQuote: 60, costPerLead: 60 };
const REV_PER_HR_DEFAULT = 150, LOST_DEFAULT = 10;

const chipS = (col: string, bg: string) => ({ fontSize: 10.5, color: col, background: bg, borderColor: "transparent", padding: "2px 8px" });

function BudgetSection({ store, go }: any) {
  const settings = store.settings || {};
  const b = settings.budget || {};
  const derived = React.useMemo(() => derivedRatios(store), [store.quotes]);
  const comm = React.useMemo(() => commissionRate(store.quotes || [], settings.salesReps || [], Date.now()), [store.quotes, settings.salesReps]);
  // effective values: saved → derived from your quotes / reps → sane trade defaults
  const overheadMo = b.overheadMo != null ? +b.overheadMo : 12000;
  const laborPct = b.laborPct != null ? +b.laborPct : (derived.laborPct != null ? Math.round(derived.laborPct) : 28);
  const matPct = b.matPct != null ? +b.matPct : (derived.matPct != null ? Math.round(derived.matPct) : 32);
  const netPct = b.netPct != null ? +b.netPct : 18;
  const commPct = b.commPct != null ? +b.commPct : comm.pct;
  // editing a bucket saves all four as shown (as before); commission is only
  // frozen once the owner types one, so it keeps following the reps. Planner
  // extras (season, scenarios, funnel, capacity, split) save just themselves,
  // so touching the season never pins labor/material % that are still
  // following your quotes.
  const setBucket = (patch: any) => store.setSettings({ budget: { ...b, overheadMo, laborPct, matPct, netPct, ...patch } });
  const setB = (patch: any) => store.setSettings({ budget: { ...b, ...patch } });
  const pb = planBuckets({ overheadMo, laborPct, matPct, commPct, netPct });

  const year = new Date().getFullYear();
  const nowMonth = new Date().getMonth();
  const cur = year * 12 + nowMonth;

  // ---- seasonality: saved edits → your last 24 months → the typical curve ----
  const rev = React.useMemo(() => revenueByMonth(store.workOrders || [], store.quotes || []), [store.workOrders, store.quotes]);
  const hist = React.useMemo(() => seasonFromHistory(rev.done, Date.now()), [rev]);
  const saved = Array.isArray(b.season) && b.season.length === 12 ? b.season : null;
  const seasonMode = saved ? (b.seasonFrom === "typical" ? "typical" : "custom") : (hist.weights ? "history" : "typical");
  const weights = normalizeSeason(saved || hist.weights || TYPICAL_SEASON);

  // ---- scenario (a view choice — which one you're looking at isn't saved) ----
  const [scenId, setScenId] = React.useState<any>("base");
  const scenOver = b.scenarios || {};
  const scenOf = (id: string) => { const s0: any = SCENARIOS.find(s => s.id === id) || SCENARIOS[0]; return id === "base" ? s0 : { ...s0, ...(scenOver[id] || {}) }; };
  const scen = scenOf(scenId);
  const plan = pb.annualGoal != null ? monthlyPlan(pb.annualGoal, weights, scen.revMult) : Array(12).fill(0);
  const planYear = plan.reduce((a: number, x: number) => a + x, 0);

  // ---- capacity: crews × workdays × hours, less weather/holiday days ----
  const crews = settings.crews || [];
  const lostPct = b.lostPct != null ? +b.lostPct : LOST_DEFAULT;
  const rate = React.useMemo(() => crewHourRate(store), [store.quotes, settings]);
  const revPerHr = b.revPerHr != null ? +b.revPerHr : (rate.rate != null ? Math.round(rate.rate) : REV_PER_HR_DEFAULT);
  const capHours = MONTH_LB.map((_, m) => (crews.length ? crewMonthHours(crews, year, m, settings.workdays, lostPct) : null));
  const capacity = capHours.map((h) => (h != null ? h * revPerHr : null));
  const overCap = MONTH_LB.map((_, m) => capacity[m] != null && plan[m] > (capacity[m] as number) + 0.5);

  // ---- track: completed / scheduled this year ----
  const done = MONTH_LB.map((_, m) => rev.done[year * 12 + m] || 0);
  const sched = MONTH_LB.map((_, m) => rev.sched[year * 12 + m] || 0);
  // won revenue not on the calendar yet — the relief valve for thin months.
  // Signed quotes only: a PAID quote with no work order already counts as
  // completed revenue (revenueByMonth), so it isn't backlog as well.
  const backlog = React.useMemo(() => {
    const wos = store.workOrders || [];
    let t = 0;
    wos.forEach((wo: any) => { if (!wo.scheduledStart && !/^(done|completed)$/.test(wo.status || "")) t += Number(wo.total) || 0; });
    (store.quotes || []).forEach((q: any) => {
      if (q.status === "signed" && !wos.some((w: any) => w.quoteId === q.id)) t += Number(q.total) || 0;
    });
    return t;
  }, [store.workOrders, store.quotes]);

  const [tab0, setTab] = useLocalPref("fw_budget_tab", "plan");
  const tab = ["plan", "track", "back", "profit"].includes(tab0) ? tab0 : "plan";
  const months = MONTH_LB.map((label, m) => ({ label, plan: plan[m], actual: done[m], sched: sched[m], cap: capacity[m] }));

  return (
    <div className="card pad" style={{ marginBottom: 18 }}>
      <div className="row between" style={{ marginBottom: 4, flexWrap: "wrap", gap: 10 }}>
        <h3 className="row gap-8" style={{ margin: 0, fontSize: 16 }}>Goal planner — the four buckets</h3>
        {derived.n > 0 && <span className="chip" style={{ fontSize: 11 }}>labor/material % prefilled from your last {derived.n} quotes</span>}
      </div>
      <p className="muted" style={{ fontSize: 12.5, margin: "0 0 14px" }}>Overhead, labor, commission, materials and target net set the revenue the year needs. Season, capacity and what's booked come straight from your jobs, crews and quotes — nothing to retype.</p>

      <div className="bz-tiles">
        <BucketTile label="Magic number" hint="What each $1 of revenue costs you in labor, commission and materials">
          <div className="display tnum" style={{ fontSize: 26 }}>{pb.magic.toFixed(2)}</div>
          <div className="muted" style={{ fontSize: 11.5 }}>Labor {laborPct}% + Comm {commPct}% + Mat {matPct}%</div>
        </BucketTile>
        <BucketTile label="Overhead" hint="Fixed monthly costs — rent, trucks, insurance, office">
          <NumInput value={overheadMo} step={250} width={84} suffix="/mo" label="Overhead per month" onCommit={(v: any) => setBucket({ overheadMo: v == null ? 12000 : v })} />
        </BucketTile>
        <BucketTile label="Labor & comm." hint={comm.source === "won" ? `Commission: your reps' rates on ${BZ_money0(comm.revenue)} won in the last 12 months` : comm.source === "roster" ? "Commission: the average of your reps' rates (nothing won yet)" : "No reps with a commission rate yet (Sales Team) — 0% until you add one"}>
          <div className="row gap-8" style={{ flexWrap: "wrap" }}>
            <NumInput value={laborPct} step={0.5} width={48} suffix="% labor" label="Labor percent" onCommit={(v: any) => setBucket({ laborPct: v == null ? 28 : Math.min(90, v) })} />
            <NumInput value={commPct} step={0.5} width={48} suffix="% comm." label="Commission percent" onCommit={(v: any) => setBucket({ commPct: v == null ? null : Math.min(50, v) })} />
          </div>
          {b.commPct != null && <button className="btn btn-ghost btn-sm" style={{ marginTop: 6, padding: "3px 8px", fontSize: 11 }} onClick={() => setBucket({ commPct: null })}>Use my reps' rates ({comm.pct}%)</button>}
        </BucketTile>
        <BucketTile label="Materials" hint="Materials as % of revenue">
          <NumInput value={matPct} step={0.5} width={52} suffix="% mat" label="Materials percent" onCommit={(v: any) => setBucket({ matPct: v == null ? 32 : Math.min(90, v) })} />
        </BucketTile>
        <BucketTile label="Target net" hint="Your planned profit margin after everything" accent="var(--teal-tint-2)">
          <NumInput value={netPct} step={0.5} width={52} suffix="% net" label="Target net percent" onCommit={(v: any) => setBucket({ netPct: v == null ? 18 : Math.min(60, v) })} />
        </BucketTile>
      </div>

      {/* revenue goal banner */}
      <div className="card pad" style={{ marginTop: 12, borderColor: pb.revGoalMo ? "var(--teal-tint-2)" : "var(--red-tint)", background: "var(--surface-2)" }}>
        {pb.revGoalMo ? (
          <div className="row between" style={{ flexWrap: "wrap", gap: 10 }}>
            <div>
              <div className="kicker">Revenue goal</div>
              <div className="display tnum" style={{ fontSize: 28, color: "var(--teal)" }}>{BZ_money0(pb.revGoalMo * 12)}<span className="muted" style={{ fontSize: 14, fontWeight: 500 }}> / year</span></div>
              <div className="muted" style={{ fontSize: 12 }}>✓ {netPct}% net margin at this revenue · {BZ_money0(pb.revGoalMo)} per month on average</div>
            </div>
            <div className="bz-be">
              <div className="kicker">Break-even</div>
              <div className="display tnum" style={{ fontSize: 20 }}>{pb.breakevenMo ? BZ_money0(pb.breakevenMo) : "—"}<span className="muted" style={{ fontSize: 12.5, fontWeight: 500 }}> / mo</span></div>
              <div className="muted" style={{ fontSize: 11.5 }}>covers overhead, $0 profit</div>
            </div>
          </div>
        ) : (
          <p style={{ margin: 0, color: "var(--red)", fontSize: 13 }}>Labor + commission + materials + target net add to ≥ 100% of revenue — this plan can't fund itself. Trim a bucket.</p>
        )}
      </div>

      {pb.revGoalMo && (
        <>
          {/* scenario */}
          <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center", margin: "14px 0 4px" }}>
            <span className="kicker">Scenario</span>
            <Seg label="Scenario" value={scenId} onChange={setScenId} options={SCENARIOS.map(s => ({ id: s.id, label: s.label }))} />
            {scenId === "base"
              ? <span className="faint" style={{ fontSize: 11.5 }}>The goal as set.</span>
              : <span className="row gap-8" style={{ flexWrap: "wrap", fontSize: 12 }}>
                  <NumInput value={Math.round(scen.revMult * 100)} step={5} width={44} suffix="% of goal" label="Revenue as percent of goal"
                    onCommit={(v: any) => setB({ scenarios: { ...scenOver, [scenId]: { ...scenOver[scenId], revMult: v == null ? scenOf(scenId).revMult : Math.min(300, v) / 100 } } })} />
                  <NumInput value={scen.winDelta} min={-50} step={1} width={40} suffix="pts win rate" label="Win rate change in points"
                    onCommit={(v: any) => setB({ scenarios: { ...scenOver, [scenId]: { ...scenOver[scenId], winDelta: v == null ? 0 : Math.max(-50, Math.min(50, v)) } } })} />
                </span>}
          </div>
          {scenId !== "base" && (() => {
            const net = planYear * (1 - pb.magic) - 12 * overheadMo;
            return <p className="muted" style={{ fontSize: 12, margin: "4px 0 0" }}>At {BZ_money0(planYear)} this year: planned net <b style={{ color: net >= 0 ? "var(--green)" : "var(--red)" }}>{signed0(net)}</b>{planYear > 0 ? ` (${Math.round((net / planYear) * 100)}% margin)` : ""} — vs {BZ_money0(pb.revGoalMo * 12 * pb.netFrac)} at the goal.</p>;
          })()}

          <div style={{ margin: "14px 0 12px" }}>
            <Seg label="Planner view" value={tab} onChange={setTab} options={[
              { id: "plan", label: "Plan" }, { id: "track", label: "Track" }, { id: "back", label: "Work backwards" }, { id: "profit", label: "Profit & cash" }]} />
          </div>

          {tab === "plan" && <PlanView {...{ months, pb, year, nowMonth, weights, seasonMode, hist, setB, crews, capHours, capacity, overCap, plan, lostPct, revPerHr, rate, b, go }} />}
          {tab === "track" && <TrackView {...{ months, pb, year, nowMonth, cur, overCap, backlog }} />}
          {tab === "back" && <BackView {...{ store, settings, b, setB, plan, planYear, done, sched, nowMonth, scen }} />}
          {tab === "profit" && <ProfitView {...{ plan, pb, overheadMo, b, setB }} />}
        </>
      )}
    </div>
  );
}

/* ---- Plan: the chart, the season, the crews ---- */
function PlanView({ months, pb, year, nowMonth, weights, seasonMode, hist, setB, crews, capHours, capacity, overCap, plan, lostPct, revPerHr, rate, b, go }: any) {
  const pct = weights.map((w: number) => Math.round(w * 1000) / 10);
  const bump = (m: number, d: number) => setB({ season: setSeasonMonth(weights.map((w: number) => w * 100), m, pct[m] + d), seasonFrom: "custom" });
  const over = MONTH_LB.filter((_, m) => overCap[m]);
  const heads = crews.reduce((a: number, c: any) => a + (Array.isArray(c.members) && c.members.length ? c.members.length : (Number(c.size) || 0)), 0);
  const capYear = capacity.every((x: any) => x == null) ? null : capacity.reduce((a: number, x: any) => a + (x || 0), 0);
  return (
    <div>
      <PlanChart months={months} breakeven={pb.breakevenMo} required={pb.revGoalMo} nowMonth={nowMonth} year={year} />

      {/* seasonality */}
      <div className="row between" style={{ margin: "18px 0 6px", flexWrap: "wrap", gap: 8 }}>
        <span className="kicker">Seasonality · share of the year by month</span>
        <span className="row gap-8" style={{ flexWrap: "wrap" }}>
          {hist.weights && seasonMode !== "history" && <button className="btn btn-ghost btn-sm" onClick={() => setB({ season: null, seasonFrom: null })}><Icon name="undo" size={12} />Reset to my history</button>}
          {seasonMode !== "typical" && <button className="btn btn-ghost btn-sm" onClick={() => setB({ season: TYPICAL_SEASON.slice(), seasonFrom: "typical" })}>Reset to typical</button>}
        </span>
      </div>
      <p className="faint" style={{ fontSize: 11.5, margin: "0 0 8px" }}>
        {seasonMode === "history" ? `From your own completed jobs — the last 24 months (${hist.monthsWithData} months with revenue).`
          : seasonMode === "custom" ? "Your edits. Moving one month rebalances the others so the year still adds to 100%."
          : `A typical fence year — slow Dec–Feb, peak May–Sep. ${hist.weights ? "Your own history is available: reset to use it." : `Your own takes over once you have 8 months of completed revenue (you have ${hist.monthsWithData}).`}`}
      </p>
      <div className="bz-season">
        {MONTH_LB.map((lb, m) => (
          <div key={lb} className="bz-season-cell" style={{ borderColor: m === nowMonth ? "var(--teal-tint-2)" : "var(--line)" }}>
            <div className="row between nowrap"><span style={{ fontWeight: m === nowMonth ? 800 : 600, fontSize: 12 }}>{lb}</span><span className="tnum" style={{ fontSize: 12, fontWeight: 700 }}>{pct[m].toFixed(1)}%</span></div>
            <div className="faint tnum" style={{ fontSize: 10.5, margin: "2px 0 5px" }}>{BZ_money0(plan[m])}</div>
            <div className="row" style={{ gap: 4 }}>
              <button className="bz-step" aria-label={`Less in ${lb}`} disabled={pct[m] <= 0} onClick={() => bump(m, -0.5)}>−</button>
              <button className="bz-step" aria-label={`More in ${lb}`} disabled={pct[m] >= 40} onClick={() => bump(m, 0.5)}>+</button>
            </div>
          </div>
        ))}
      </div>

      {/* capacity */}
      <div className="kicker" style={{ margin: "18px 0 6px" }}>Crew capacity</div>
      {crews.length === 0 ? (
        <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>No crews yet — <a style={{ color: "var(--teal)", cursor: "pointer" }} onClick={() => go && go("settings", { section: "crews" })}>add one in Settings → Crews</a> (headcount, hours per day, workdays) and each month shows what your crews can actually install.</p>
      ) : (
        <>
          <div className="row gap-12" style={{ flexWrap: "wrap", alignItems: "center", fontSize: 12.5 }}>
            <span className="muted">{crews.length} crew{crews.length === 1 ? "" : "s"} · {heads} {heads === 1 ? "person" : "people"} · {Math.round(capHours[nowMonth] || 0).toLocaleString("en-US")} crew-hours in {MONTH_LB[nowMonth]}</span>
            <span className="row gap-8" style={{ alignItems: "center" }}><span className="faint" style={{ fontSize: 11.5 }}>Days lost to weather & holidays</span>
              <NumInput value={lostPct} step={1} width={40} suffix="%" label="Days lost percent" onCommit={(v: any) => setB({ lostPct: v == null ? null : Math.min(90, v) })} /></span>
            <span className="row gap-8" style={{ alignItems: "center" }}><span className="faint" style={{ fontSize: 11.5 }}>Revenue per crew-hour</span>
              <NumInput value={revPerHr} prefix="$" step={5} width={56} label="Revenue per crew-hour" onCommit={(v: any) => setB({ revPerHr: v })} /></span>
          </div>
          <p className="faint" style={{ fontSize: 11.5, margin: "6px 0 0" }}>
            Crew-hours = headcount × hours per day × each crew's workdays in the month, less the days lost. Revenue per crew-hour {b.revPerHr != null
              ? <>is yours — <a style={{ color: "var(--teal)", cursor: "pointer" }} onClick={() => setB({ revPerHr: null })}>use my quotes{rate.rate != null ? ` (${BZ_money0(rate.rate)})` : ""}</a></>
              : rate.rate != null ? `comes from ${rate.n} ${rate.won ? "won jobs" : "recent quotes"}: total ÷ quoted crew-hours (hours on site × crew size).` : "is a default — edit it, or it fills in once quotes carry labor hours."}
            {capYear != null ? ` Full year: about ${BZ_money0(capYear)} of install capacity.` : ""}
          </p>
          {over.length > 0
            ? <div className="pe-callout warn" style={{ marginTop: 10, padding: "10px 14px" }}><p style={{ margin: 0, fontSize: 12.5 }}><b style={{ color: "var(--amber)" }}>{over.join(", ")}:</b> the plan is more than your crews can install. Hire or add a crew before then, or push work into slower months.</p></div>
            : <p style={{ fontSize: 12.5, margin: "8px 0 0", color: "var(--green)" }}>✓ Your crews can cover the plan every month.</p>}
        </>
      )}
    </div>
  );
}

/* ---- Track: plan vs what's completed and scheduled, automatically ---- */
function TrackView({ months, pb, year, nowMonth, overCap, backlog }: any) {
  const upTo = months.slice(0, nowMonth + 1);
  const planYtd = upTo.reduce((a: number, m: any) => a + m.plan, 0);
  const doneYtd = upTo.reduce((a: number, m: any) => a + m.actual, 0);
  const ahead = months.slice(nowMonth).reduce((a: number, m: any) => a + m.sched, 0);
  const pace = planYtd > 0 ? Math.round((doneYtd / planYtd) * 100) : null;
  const tot = (k: string) => months.reduce((a: number, m: any) => a + m[k], 0);
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 12 }}>
        {[["Plan to date", BZ_money0(planYtd), `Jan–${MONTH_LB[nowMonth]}`, null],
          ["Completed to date", BZ_money0(doneYtd), pace != null ? `${pace}% of plan` : "—", pace != null && pace >= 100 ? "var(--green)" : pace != null && pace >= 85 ? "var(--amber)" : "var(--red)"],
          ["Scheduled ahead", BZ_money0(ahead), "on the calendar, not done", "var(--teal)"],
          ["Won, not scheduled", BZ_money0(backlog), "signed work to put on the calendar", backlog > 0 ? "var(--amber)" : null]].map(([lb, v, sub, col]: any) => (
          <div key={lb} className="card pad" style={{ padding: "11px 13px" }}>
            <div className="kicker" style={{ marginBottom: 4 }}>{lb}</div>
            <div className="display tnum" style={{ fontSize: 19, color: col || "var(--fg)" }}>{v}</div>
            <div className="muted" style={{ fontSize: 11 }}>{sub}</div>
          </div>
        ))}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="rep-src-table">
          <thead><tr><th>Month</th><th className="num">Plan</th><th className="num">Completed</th><th className="num">Scheduled</th><th className="num">Gap to plan</th><th>vs break-even</th></tr></thead>
          <tbody>
            {months.map((m: any, i: number) => {
              const booked = m.actual + m.sched;
              const gap = m.plan - booked;
              const s = stressOf(pb.breakevenMo ? booked / pb.breakevenMo : 0);
              const isNow = i === nowMonth, past = i < nowMonth;
              return (
                <tr key={m.label} style={{ background: isNow ? "var(--surface-2)" : "transparent" }}>
                  <td style={{ fontWeight: isNow ? 800 : 600, color: past ? "var(--fg-2)" : "var(--fg)" }}>{isNow ? "● " : ""}{m.label}</td>
                  <td className="num tnum">{BZ_money0(m.plan)}</td>
                  <td className="num tnum" style={{ fontWeight: 700, color: m.actual > 0 ? "var(--fg)" : "var(--fg-3)" }}>{m.actual > 0 ? BZ_money0(m.actual) : "—"}</td>
                  <td className="num tnum" style={{ color: m.sched > 0 ? "var(--teal)" : "var(--fg-3)" }}>{m.sched > 0 ? BZ_money0(m.sched) : "—"}</td>
                  <td className="num tnum" style={{ color: gap <= 0 ? "var(--green)" : past ? "var(--red)" : "var(--fg-2)" }}>{gap <= 0 ? "✓ +" + BZ_money0(-gap) : BZ_money0(gap) + " short"}</td>
                  <td style={{ whiteSpace: "nowrap" }}><span className="chip" style={chipS(s.col, s.bg)}>{s.label}</span>{overCap[i] && <span className="chip" style={{ ...chipS("var(--amber)", "var(--amber-tint)"), marginLeft: 6 }}>OVER CAPACITY</span>}</td>
                </tr>
              );
            })}
            <tr>
              <td style={{ fontWeight: 800 }}>{year}</td>
              <td className="num tnum" style={{ fontWeight: 800 }}>{BZ_money0(tot("plan"))}</td>
              <td className="num tnum" style={{ fontWeight: 800 }}>{BZ_money0(tot("actual"))}</td>
              <td className="num tnum" style={{ fontWeight: 800, color: "var(--teal)" }}>{BZ_money0(tot("sched"))}</td>
              <td className="num tnum" style={{ fontWeight: 800 }}>{(() => { const g = tot("plan") - tot("actual") - tot("sched"); return g <= 0 ? "✓ +" + BZ_money0(-g) : BZ_money0(g) + " short"; })()}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="faint" style={{ fontSize: 11.5, margin: "8px 0 0" }}>Completed = work orders marked complete, in the month they finished (plus paid jobs with no work order). Scheduled = work orders on the calendar, by install start. The status compares both against break-even ({pb.breakevenMo ? BZ_money0(pb.breakevenMo) : "—"}/mo).</p>
    </div>
  );
}

/* ---- Work backwards: revenue → jobs → quotes → leads → marketing ---- */
function BackView({ store, settings, b, setB, plan, planYear, done, sched, nowMonth, scen }: any) {
  const spendMo: number = Object.values(settings.marketingSpend || {}).reduce((a: number, v: any) => a + (Number(v) || 0), 0) as number;
  const f = React.useMemo(() => funnelStats({ quotes: store.quotes || [], customers: store.customers || [], spendMo, nowMs: Date.now() }), [store.quotes, store.customers, spendMo]);
  const own = b.funnel || {};
  const setF = (k: string, v: any) => setB({ funnel: { ...own, [k]: v } });
  // each input: yours → your records → default
  const pick = (k: string, fromData: any, scale: number) => own[k] != null
    ? { value: +own[k], src: "you", n: 0 }
    : fromData ? { value: Math.round(fromData.value * scale * 10) / 10, src: fromData.days === 90 ? "90" : "365", n: fromData.n } : { value: FUNNEL_DEFAULTS[k], src: "default", n: 0 };
  const avgJob = pick("avgJob", f.avgJob, 1), winRate = pick("winRate", f.winRate, 100), l2q = pick("leadToQuote", f.leadToQuote, 100), cpl = pick("costPerLead", f.costPerLead, 1);
  avgJob.value = Math.round(avgJob.value); cpl.value = Math.round(cpl.value);
  const winEff = Math.max(1, Math.min(100, winRate.value + (scen.winDelta || 0)));
  const assume = { avgJob: avgJob.value, winRate: winEff / 100, leadToQuote: l2q.value / 100, costPerLead: cpl.value };
  const booked = done.reduce((a: number, x: number) => a + x, 0) + sched.reduce((a: number, x: number) => a + x, 0);
  const cols = [
    { label: `${MONTH_LB[nowMonth]} (this month)`, goal: plan[nowMonth] },
    { label: "Full year", goal: planYear },
    { label: "Still to book this year", goal: Math.max(0, planYear - booked) },
  ].map((c) => ({ ...c, ...workBackwards(c.goal, assume) }));
  const srcText = (p: any, what: string) => p.src === "you" ? "you set this" : p.src === "default" ? "default — edit" : `from your last ${p.src === "90" ? "90 days" : "12 months"} (${p.n} ${what})`;
  const card = (label: string, p: any, k: string, input: any, what: string, extra?: any) => (
    <div className="card pad" style={{ padding: "11px 13px" }}>
      <div className="kicker" style={{ marginBottom: 6 }}>{label}</div>
      {input}
      <div className="faint" style={{ fontSize: 10.5, marginTop: 5 }}>{srcText(p, what)}{p.src === "you" && <> · <a style={{ color: "var(--teal)", cursor: "pointer" }} onClick={() => setF(k, null)}>reset</a></>}{extra}</div>
    </div>
  );
  const n0 = (v: any) => (v == null ? "—" : Math.round(v).toLocaleString("en-US"));
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 12 }}>
        {card("Average job", avgJob, "avgJob", <NumInput value={avgJob.value} prefix="$" step={250} width={70} label="Average job" onCommit={(v: any) => setF("avgJob", v)} />, "won jobs")}
        {card("Win rate", winRate, "winRate", <NumInput value={winRate.value} step={1} width={52} suffix="%" label="Win rate" onCommit={(v: any) => setF("winRate", v == null ? null : Math.min(100, v))} />, "quotes",
          scen.winDelta ? <> · {scen.label}: {winEff}%</> : null)}
        {card("Lead → quote", l2q, "leadToQuote", <NumInput value={l2q.value} step={1} width={52} suffix="%" label="Lead to quote rate" onCommit={(v: any) => setF("leadToQuote", v == null ? null : Math.min(100, v))} />, "new customers")}
        {card("Cost per lead", cpl, "costPerLead", <NumInput value={cpl.value} prefix="$" step={5} width={56} label="Cost per lead" onCommit={(v: any) => setF("costPerLead", v)} />, "new customers",
          cpl.src !== "you" && spendMo > 0 ? <> · {BZ_money0(spendMo)}/mo spend in Reports</> : null)}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="rep-src-table" style={{ minWidth: 520 }}>
          <thead><tr><th></th>{cols.map((c) => <th key={c.label} className="num">{c.label}</th>)}</tr></thead>
          <tbody>
            <tr><td className="muted">Revenue</td>{cols.map((c) => <td key={c.label} className="num tnum" style={{ fontWeight: 800 }}>{BZ_money0(c.goal)}</td>)}</tr>
            <tr><td className="muted">÷ {BZ_money0(avgJob.value)} avg job → <b style={{ color: "var(--fg)" }}>jobs</b></td>{cols.map((c) => <td key={c.label} className="num tnum" style={{ fontWeight: 700 }}>{n0(c.jobs)}</td>)}</tr>
            <tr><td className="muted">÷ {winEff}% win rate → <b style={{ color: "var(--fg)" }}>quotes</b></td>{cols.map((c) => <td key={c.label} className="num tnum" style={{ fontWeight: 700 }}>{n0(c.quotes)}</td>)}</tr>
            <tr><td className="muted">÷ {l2q.value}% lead → quote → <b style={{ color: "var(--fg)" }}>leads</b></td>{cols.map((c) => <td key={c.label} className="num tnum" style={{ fontWeight: 700 }}>{n0(c.leads)}</td>)}</tr>
            <tr><td className="muted">× {BZ_money0(cpl.value)} per lead → <b style={{ color: "var(--fg)" }}>marketing</b></td>{cols.map((c) => <td key={c.label} className="num tnum" style={{ fontWeight: 800, color: "var(--teal)" }}>{c.marketing == null ? "—" : BZ_money0(c.marketing)}</td>)}</tr>
          </tbody>
        </table>
      </div>
      <p className="faint" style={{ fontSize: 11.5, margin: "8px 0 0" }}>Counted the way Reports counts them: win rate = won ÷ quotes sent; a lead is a new customer record. Monthly ad spend typed in Reports → Lead sources turns into cost per lead here. Still to book = the year's plan less what's completed and scheduled.</p>
    </div>
  );
}

/* ---- Profit & cash: quarterly net and where it goes ---- */
function ProfitView({ plan, pb, overheadMo, b, setB }: any) {
  const q = quarterlyPlan(plan, pb.magic, overheadMo);
  const yearNet = q[4].net;
  const split = Array.isArray(b.split) && b.split.length ? b.split : DEFAULT_SPLIT;
  const alloc = allocateProfit(yearNet, split);
  const setPct = (id: string, v: any) => setB({ split: split.map((s: any) => (s.id === id ? { ...s, pct: v == null ? 0 : Math.min(100, v) } : s)) });
  const fix = () => { const d = 100 - alloc.total; const last = split[split.length - 1]; setPct(last.id, Math.max(0, (Number(last.pct) || 0) + d)); };
  const row = (lb: string, k: string, style?: any, neg?: boolean) => (
    <tr><td className="muted" style={style}>{lb}</td>{q.map((r: any) => <td key={r.label} className="num tnum" style={{ ...(style || {}), ...(k === "net" ? { color: r.net >= 0 ? "var(--green)" : "var(--red)" } : {}) }}>{neg ? "−" + BZ_money0(r[k]) : signed0(r[k])}</td>)}</tr>
  );
  return (
    <div>
      <div className="kicker" style={{ marginBottom: 6 }}>Quarterly cash flow · planned</div>
      <div style={{ overflowX: "auto" }}>
        <table className="rep-src-table" style={{ minWidth: 520 }}>
          <thead><tr><th></th>{q.map((r: any) => <th key={r.label} className="num">{r.label}</th>)}</tr></thead>
          <tbody>
            {row("Revenue", "revenue", { fontWeight: 700 })}
            {row(`Labor, commission & materials (${Math.round(pb.magic * 100)}%)`, "variable", undefined, true)}
            {row("Overhead", "overhead", undefined, true)}
            {row("Planned net", "net", { fontWeight: 800 })}
          </tbody>
        </table>
      </div>
      <p className="faint" style={{ fontSize: 11.5, margin: "6px 0 16px" }}>{q.slice(0, 4).some((r: any) => r.net < 0)
        ? `${q.slice(0, 4).filter((r: any) => r.net < 0).map((r: any) => r.label).join(" and ")} run${q.slice(0, 4).filter((r: any) => r.net < 0).length === 1 ? "s" : ""} negative — overhead doesn't take the winter off. The cash reserve below and the weekly cash forecast are how you carry it.`
        : "Every quarter clears its overhead at this plan. Planned net is what is left after every bucket — the split below is where it goes."}</p>

      <div className="row between" style={{ flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
        <span className="kicker">Profit allocation · {signed0(yearNet)} planned net</span>
        {alloc.ok ? <span className="chip" style={chipS("var(--green)", "var(--green-tint)")}>adds to 100%</span>
          : <span className="row gap-8"><span className="chip" style={chipS("var(--amber)", "var(--amber-tint)")}>adds to {alloc.total}% — needs 100%</span><button className="btn btn-ghost btn-sm" onClick={fix}>Put the difference in {split[split.length - 1].label}</button></span>}
      </div>
      {yearNet <= 0
        ? <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>No planned profit to split under this scenario.</p>
        : (
          <div style={{ display: "grid", gap: 6 }}>
            {alloc.rows.map((r: any) => (
              <div key={r.id} className="ready-row" style={{ justifyContent: "space-between" }}>
                <span style={{ fontWeight: 700, minWidth: 110 }}>{r.label}</span>
                <NumInput value={r.pct} step={1} width={42} suffix="%" label={r.label + " percent"} onCommit={(v: any) => setPct(r.id, v)} />
                <span className="tnum" style={{ marginLeft: "auto", fontWeight: 800 }}>{r.amount == null ? "—" : BZ_money0(r.amount)}<span className="muted" style={{ fontWeight: 500, fontSize: 11.5 }}>{r.amount == null ? "" : ` · ${BZ_money0(r.amount / 12)}/mo`}</span></span>
              </div>
            ))}
          </div>
        )}
      <p className="faint" style={{ fontSize: 11.5, margin: "8px 0 0" }}>A starting split — change any line; it only counts once the lines add to 100%. Ask your accountant what to hold for taxes.</p>
    </div>
  );
}

/* ---- weekly cash forecast ---------------------------------------------- */
const CASH_FLAGS: any = {
  healthy: { label: "HEALTHY", col: "var(--green)", bg: "var(--green-tint)" },
  near:    { label: "NEAR FLOOR", col: "var(--amber)", bg: "var(--amber-tint)" },
  below:   { label: "BELOW FLOOR", col: "var(--red)", bg: "var(--red-tint)" },
};
const CASH_SCEN = [
  { id: "base", label: "Base 100%", short: "Base", mult: 1 },
  { id: "conservative", label: "Conservative 90%", short: "Cons. 90%", mult: 0.9 },
  { id: "upside", label: "Upside 110%", short: "Up 110%", mult: 1.1 },
];
const mdLabel = (s: string) => new Date(s + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
function weekRange(w: any) {
  const a = new Date(w.start + "T12:00:00"), z = new Date(w.end + "T12:00:00");
  return mdLabel(w.start) + "–" + (a.getMonth() === z.getMonth() ? String(z.getDate()) : mdLabel(w.end));
}

function WeekFlags({ w }: any) {
  const f = CASH_FLAGS[w.status];
  return (
    <span className="row" style={{ gap: 4, flexWrap: "wrap" }}>
      <span className="chip" style={chipS(f.col, f.bg)}>{f.label}</span>
      {w.payroll && <span className="chip" style={chipS("var(--violet)", "var(--violet-tint)")}>PAYROLL WEEK</span>}
    </span>
  );
}
/* the individual moves behind a week: who pays in, who gets paid */
function WeekMoves({ w, scen }: any) {
  if (!w.moves.length) return <span className="muted" style={{ fontSize: 12.5 }}>Nothing moves this week.</span>;
  return (
    <div style={{ display: "grid", gap: 3 }}>
      {w.moves.map((m: any, j: number) => (
        <div key={j} className="row between nowrap" style={{ fontSize: 12.5, gap: 10, alignItems: "flex-start" }}>
          <span style={{ minWidth: 0 }}><span style={{ color: m.dir === "in" ? "var(--green)" : "var(--fg-3)", fontWeight: 800, marginRight: 6 }}>{m.dir === "in" ? "+" : "−"}</span>{m.label}{m.dir === "in" && m.expected !== false && scen.mult !== 1 ? <span className="faint"> · at {Math.round(scen.mult * 100)}%</span> : ""}</span>
          <span className="tnum" style={{ fontWeight: 700, color: m.dir === "in" ? "var(--green)" : "var(--fg)", flexShrink: 0 }}>{m.dir === "in" ? "+" : "−"}{BZ_money0(m.amount)}</span>
        </div>
      ))}
    </div>
  );
}

function CashSection({ store, go }: any) {
  const settings = store.settings || {};
  const c = settings.cash || {};
  const startBalance = c.startBalance != null ? +c.startBalance : null;
  const floor = c.floor != null ? +c.floor : 5000;
  const setC = (patch: any) => store.setSettings({ cash: { ...c, startBalance, floor, ...patch } });
  const b = settings.budget || {};
  const overheadMo = b.overheadMo != null ? +b.overheadMo : 12000;

  const [horizon, setHorizon] = useLocalPref("fw_cash_weeks", "13");
  const [scenId, setScenId] = useLocalPref("fw_cash_scenario", "base");
  const WEEKS = [13, 26, 52].includes(+horizon) ? +horizon : 13;
  const scen = CASH_SCEN.find(s => s.id === scenId) || CASH_SCEN[0];
  const [open, setOpen] = React.useState<any>(null);
  const narrow = useNarrowScreen(560);

  /* The overhead rule. The budget's overhead/mo is every fixed cost; the
     recurring costs listed here are PART of it. So the forecast charges
     "overhead not covered by your schedules" = budget overhead/mo − the
     monthly equivalent of the recurring list (never below 0), spread evenly
     by week. With nothing listed that is the whole overhead (the old
     behavior); once the list covers it, the line is $0. Payroll and loan
     payments never reduce it: payroll pays labor (its own bucket) and loan
     principal isn't overhead. A payroll schedule instead REPLACES the
     job-by-job labor lines — payroll is how that labor actually leaves the
     bank, so counting both would pay the crew twice. */
  const recurringMo = (c.recurring || []).reduce((a: number, r: any) => a + monthlyEquivalent(r.amount, r.cadence), 0);
  const overheadGapMo = Math.max(0, overheadMo - recurringMo);
  const overheadWk = (overheadGapMo * 12) / 52;
  const hasPayroll = (c.payroll || []).some((p: any) => Number(p.amount) > 0);

  const today = ymdOf(new Date());
  const w0 = mondayOf(today);
  // cash today is "as of" when it was entered: money that moved after that
  // (payments recorded, schedule dates passed) still counts; before it, it's
  // already in the balance. Balances saved before the date was kept → today.
  const asOfMs = c.startAt ? Number(c.startAt) : null;
  const asOfYmd = asOfMs ? ymdOf(new Date(asOfMs)) : today;

  const model = React.useMemo(() => {
    const end = addDaysYmd(w0, WEEKS * 7);
    const w0ms = new Date(w0 + "T00:00:00").getTime();
    const cutoff = asOfMs != null ? asOfMs : w0ms;
    const ymdMs = (t: number) => ymdOf(new Date(t));
    const moves: any[] = [];
    const quotes = store.quotes || [], wos = store.workOrders || [];
    const who = (x: any) => x.customer || "job";

    // payments that landed after the balance was read (manual ledger + online)
    quotes.forEach((q: any) => [...(Array.isArray(q.payments) ? q.payments : []), ...(Array.isArray(q.stripePayments) ? q.stripePayments : [])].forEach((p: any) => {
      if (p && p.at && p.at > cutoff) moves.push({ date: ymdMs(p.at), dir: "in", amount: Number(p.amount) || 0, kind: "payment", label: "Payment received · " + who(q), expected: false });
    }));
    wos.forEach((wo: any) => {
      const q = wo.quoteId ? quotes.find((x: any) => x.id === wo.quoteId) : null;
      if (!/^(done|completed)$/.test(wo.status || "")) {
        // materials the week before install (or at the supplier ETA); labor on install week
        let matCost = 0, laborCost = 0;
        if (q) {
          try { const v = computeQuoteView(q, settings); matCost = Number(v && v.quote && v.quote.materialTotal) || 0; laborCost = Number(v && v.quote && v.quote.laborCost) || 0; } catch (e) {}
        }
        const startD = wo.scheduledStart || null;
        const matD = wo.materialEta || (startD ? addDaysYmd(startD, -7) : null);
        if (matD && !wo.suppliesOrdered) moves.push({ date: matD, dir: "out", amount: matCost, kind: "materials", label: "Materials · " + who(wo) });
        else if (matD && wo.suppliesOrdered && matD >= w0) moves.push({ date: matD, dir: "out", amount: matCost, kind: "materials", label: "Materials (ordered) · " + who(wo) });
        if (startD && !hasPayroll) moves.push({ date: startD, dir: "out", amount: laborCost, kind: "labor", label: "Labor · " + who(wo) });
        // the unpaid balance clears when the job wraps
        if (q) {
          const bal = Math.max(0, (Number(q.total) || 0) - quotePayments(q).paid);
          const endD = wo.scheduledEnd || startD;
          if (endD) moves.push({ date: endD, dir: "in", amount: bal, kind: "balance", label: "Balance · " + who(wo) });
        }
      } else if (q) {
        // finished & still owed → assume you chase it this week
        const bal = Math.max(0, (Number(q.total) || 0) - quotePayments(q).paid);
        moves.push({ date: w0, dir: "in", amount: bal, kind: "balance", label: "Overdue balance · " + who(wo) });
      }
    });
    // signed, no deposit yet, no work order → the deposit is expected next week
    quotes.forEach((q: any) => {
      if (q.status !== "signed" || wos.some((w: any) => w.quoteId === q.id) || quotePayments(q).paid > 0) return;
      const dep = quoteDeposit(q, settings, Number(q.total) || 0);
      moves.push({ date: addDaysYmd(w0, 7), dir: "in", amount: dep.amount, kind: "deposit", label: "Deposit · " + (q.customer || "quote") });
    });
    // the owner's schedules, from the day the balance was read
    moves.push(...scheduleMoves(c, asOfYmd < w0 ? asOfYmd : (asOfYmd > today ? today : asOfYmd), end));
    if (overheadWk > 0) for (let i = 0; i < WEEKS; i++) {
      moves.push({ date: addDaysYmd(w0, i * 7), dir: "out", amount: overheadWk, kind: "overhead", label: recurringMo > 0 ? "Overhead not covered by your schedules" : "Overhead (budget ÷ weeks)" });
    }
    const weeks = buildForecast({ weekStart: w0, weeks: WEEKS, start: startBalance || 0, floor, moves, inflowMult: scen.mult });
    return { weeks, kpi: cashKpis(weeks, startBalance || 0, floor) };
  }, [store.quotes, store.workOrders, settings, startBalance, floor, WEEKS, scen.mult, w0, today, asOfMs, asOfYmd, overheadWk, recurringMo, hasPayroll]);

  const haveBalance = startBalance != null;
  const { weeks, kpi } = model;
  const spark = React.useMemo(() => {
    if (!haveBalance) return null;
    const W = 560, H = 90, vals = weeks.map((w: any) => w.ending);
    const lo = Math.min(...vals, floor, 0), hi = Math.max(...vals, floor * 2, 1);
    const x = (i: number) => (i / Math.max(1, weeks.length - 1)) * (W - 8) + 4;
    const y = (v: number) => H - 6 - ((v - lo) / (hi - lo || 1)) * (H - 14);
    return { W, H, floorY: y(floor), zeroY: y(0), pts: vals.map((v: number, i: number) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ") };
  }, [weeks, floor, haveBalance]);
  const last = weeks[weeks.length - 1];
  const breach = kpi.breach != null ? weeks[kpi.breach] : null;
  const asOfText = asOfMs ? new Date(asOfMs).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null;
  const stale = asOfMs != null && Date.now() - asOfMs > 8 * DAY;

  return (
    <div className="card pad">
      <div className="row between" style={{ marginBottom: 4, flexWrap: "wrap", gap: 10 }}>
        <h3 className="row gap-8" style={{ margin: 0, fontSize: 16 }}>Cash forecast — next {WEEKS} weeks</h3>
        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
          <Seg label="Forecast range" value={String(WEEKS)} onChange={setHorizon} options={[{ id: "13", label: "13 wks" }, { id: "26", label: "26 wks" }, { id: "52", label: "52 wks" }]} />
          <Seg label="Cash-in scenario" value={scen.id} onChange={setScenId} options={CASH_SCEN.map(s => ({ id: s.id, label: narrow ? s.short : s.label, title: s.label + " — scales expected customer payments only" }))} />
        </div>
      </div>
      <p className="muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>Built from your jobs as they're scheduled, plus the bills, recurring costs, payroll and loans you list below. The scenario scales expected customer payments only — payments already recorded and every outflow stay as they are.</p>

      <div className="row gap-12" style={{ flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
        <span className="row gap-8" style={{ alignItems: "center" }}><span className="faint" style={{ fontSize: 11.5 }}>Cash today</span>
          <NumInput value={startBalance} prefix="$" step={500} width={84} placeholder="bank $" label="Cash today" onCommit={(v: any) => setC({ startBalance: v, startAt: v == null ? null : Date.now(), balanceSource: "manual" })} /></span>
        <span className="row gap-8" style={{ alignItems: "center" }}><span className="faint" style={{ fontSize: 11.5 }}>Floor</span>
          <NumInput value={floor} prefix="$" step={500} width={70} label="Cash floor" onCommit={(v: any) => setC({ floor: v == null ? 5000 : v })} /></span>
        {haveBalance && asOfText && <span className="faint" style={{ fontSize: 11.5, color: stale ? "var(--amber)" : undefined }}>{c.balanceSource === "quickbooks" ? "from QuickBooks" : "entered"} {asOfText}{stale ? " — update it" : ""}</span>}
      </div>
      <div style={{ marginBottom: 14 }}><QboStrip store={store} cash={c} setC={setC} go={go} /></div>

      {!haveBalance
        ? <EmptyState icon="dollar" title="Enter today's bank balance" sub="Add your cash on hand above and the weekly radar builds itself from your scheduled jobs and the schedules below." />
        : <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 12 }}>
            {[["Ending cash", signed0(kpi.ending), `end of CW${last.cw} · ${weekRange(last)}`, kpi.ending < floor ? "var(--red)" : "var(--fg)"],
              ["Weeks above floor", `${kpi.above}/${kpi.total}`, `floor ${BZ_money0(floor)} — your minimum buffer`, kpi.above === kpi.total ? "var(--green)" : kpi.above >= kpi.total * 0.75 ? "var(--amber)" : "var(--red)"],
              ["Runway", kpi.runway == null ? "Not burning" : kpi.runway >= 520 ? "10+ yrs" : kpi.runway.toFixed(1) + " wks", kpi.runway == null ? "money in covers money out, next 4 wks" : `cash today ÷ ${BZ_money0(kpi.burn)}/wk burn, next 4 wks`, kpi.runway == null || kpi.runway >= 13 ? "var(--green)" : kpi.runway >= 6 ? "var(--amber)" : "var(--red)"],
              ["First breach", breach ? `CW${breach.cw}` : "None", breach ? `${weekRange(breach)} · ends at ${signed0(breach.ending)}` : `nothing below the floor in ${WEEKS} weeks`, breach ? "var(--red)" : "var(--green)"]].map(([lb, v, sub, col]: any) => (
              <div key={lb} className="card pad" style={{ padding: "11px 13px" }}>
                <div className="kicker" style={{ marginBottom: 4 }}>{lb}</div>
                <div className="display tnum" style={{ fontSize: 20, color: col }}>{v}</div>
                <div className="muted" style={{ fontSize: 11 }}>{sub}</div>
              </div>
            ))}
          </div>
          {spark && (
            <svg width="100%" height="90" viewBox={`0 0 ${spark.W} ${spark.H}`} style={{ display: "block", marginBottom: 10 }} preserveAspectRatio="none" role="img" aria-label="Ending cash by week, with the floor">
              <line x1="0" x2={spark.W} y1={spark.floorY} y2={spark.floorY} stroke="var(--amber)" strokeDasharray="4 4" strokeWidth="1" opacity="0.7" vectorEffect="non-scaling-stroke" />
              <line x1="0" x2={spark.W} y1={spark.zeroY} y2={spark.zeroY} stroke="var(--red)" strokeDasharray="2 5" strokeWidth="1" opacity="0.6" vectorEffect="non-scaling-stroke" />
              <polyline points={spark.pts} fill="none" stroke="var(--teal)" strokeWidth="2" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
            </svg>
          )}
          <div className="kicker" style={{ margin: "4px 0 6px" }}>Weekly cash radar · tap a week for what's behind it</div>
          {narrow ? (
            /* phone: two lines per week instead of a five-column table */
            <div style={{ display: "grid", gap: 6 }}>
              {weeks.map((w: any) => {
                const isOpen = open === w.i;
                return (
                  <div key={w.i} className="bz-rl" onClick={() => setOpen(isOpen ? null : w.i)} role="button" aria-expanded={isOpen} style={{ background: isOpen ? "var(--surface-2)" : undefined }}>
                    <div className="row between nowrap" style={{ gap: 8 }}>
                      <span style={{ fontSize: 13 }}><b>CW{w.cw}</b><span className="muted"> · {w.i === 0 ? "this wk" : weekRange(w)}</span></span>
                      <span className="tnum" style={{ fontWeight: 800, fontSize: 14, color: w.ending < 0 ? "var(--red)" : "var(--fg)" }}>{signed0(w.ending)}</span>
                    </div>
                    <div className="row between" style={{ gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                      <span className="tnum" style={{ fontSize: 11.5 }}><span style={{ color: w.inflow > 0 ? "var(--green)" : "var(--fg-3)" }}>in {w.inflow > 0 ? BZ_money0(w.inflow) : "$0"}</span><span className="muted"> · out {BZ_money0(w.outflow)}</span></span>
                      <WeekFlags w={w} />
                    </div>
                    {isOpen && <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--line)" }}><WeekMoves w={w} scen={scen} /></div>}
                  </div>
                );
              })}
            </div>
          ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="rep-src-table">
              <thead><tr><th>Week</th><th className="num">In</th><th className="num">Out</th><th className="num">Ending</th><th>Flags</th></tr></thead>
              <tbody>
                {weeks.map((w: any) => {
                  const isOpen = open === w.i;
                  return (
                    <React.Fragment key={w.i}>
                      <tr className="bz-radar-row" onClick={() => setOpen(isOpen ? null : w.i)} style={{ cursor: "pointer", background: isOpen ? "var(--surface-2)" : undefined }} aria-expanded={isOpen}>
                        <td><div style={{ fontWeight: w.i === 0 ? 800 : 600 }}>CW{w.cw}{w.i === 0 ? " · this wk" : ""}</div><div className="faint" style={{ fontSize: 11 }}>{weekRange(w)}</div></td>
                        <td className="num tnum" style={{ color: w.inflow > 0 ? "var(--green)" : "var(--fg-3)" }}>{w.inflow > 0 ? BZ_money0(w.inflow) : "—"}</td>
                        <td className="num tnum" style={{ color: "var(--fg-2)" }}>{w.outflow > 0 ? BZ_money0(w.outflow) : "—"}</td>
                        <td className="num tnum" style={{ fontWeight: 800, color: w.ending < 0 ? "var(--red)" : "var(--fg)" }}>{signed0(w.ending)}</td>
                        <td><WeekFlags w={w} /></td>
                      </tr>
                      {isOpen && (
                        <tr><td colSpan={5} style={{ background: "var(--surface-2)", whiteSpace: "normal", padding: "8px 12px 12px" }}><WeekMoves w={w} scen={scen} /></td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </>}

      <p className="faint" style={{ fontSize: 11.5, margin: "12px 0 14px", lineHeight: 1.5 }}>
        <b style={{ color: "var(--fg-3)" }}>How it adds up.</b> In: deposits on signed jobs, balances when a job is scheduled to finish, and money already owed on finished jobs (this week).
        Out: materials the week before install; {hasPayroll ? "your payroll runs (job-by-job labor is left out — payroll is how it's paid)" : "labor on install week (set a payroll schedule and payroll replaces it)"}; bills, recurring costs and loan payments on their dates;
        and overhead not covered by your recurring list — {BZ_money0(overheadMo)}/mo budget overhead{recurringMo > 0 ? ` − ${BZ_money0(recurringMo)}/mo listed` : ""} = {BZ_money0(overheadWk)}/wk.
        A supplier bill for a job's materials is counted on top of that job's materials line — mark it paid or delete it if it's the same money.
      </p>
      <div className="kicker" style={{ marginBottom: 8 }}>Money-out schedules</div>
      <CashSchedules cash={c} setC={setC} />
    </div>
  );
}

/* ============================================================
   Sales Team — the rep build-out behind the dashboard leaderboard.
   Roster lives in settings.salesReps: [{ id, name, email, commissionPct,
   goalMo }]. Everything else (sent / won / close / pipeline / revenue /
   deposits / commission) computes live from store.quotes, credited by the
   `rep` stamped on each quote in the Estimator.
   ============================================================ */
const TEAM_WON = ["signed", "paid"];
const TEAM_OPEN = ["pending", "viewed"];
function teamCollected(q: any, from: number): number {
  const sumFrom = (list: any) => (Array.isArray(list) ? list : [])
    .filter((p: any) => (Number(p.at) || 0) >= from).reduce((a: number, p: any) => a + (Number(p.amount) || 0), 0);
  const manual = sumFrom(q.payments), online = sumFrom(q.stripePayments);
  if (from > 0) return manual + online;            // a real period needs dated payments
  // all-time: depositPaid already holds the Stripe sum (webhook, may carry no
  // dates) — take the larger of it and the itemized ledger, never both.
  return manual + Math.max(online, Number(q.depositPaid) || 0);
}
const repKey = (s: string) => (s || "").trim().toLowerCase();

function SalesTeamPage({ store, go, auth }: any) {
  const settings = store.settings || {};
  const roster = settings.salesReps || [];
  const quotes = store.quotes || [];
  const [period, setPeriod] = React.useState<any>("month");
  const now = new Date();
  const from = period === "month" ? new Date(now.getFullYear(), now.getMonth(), 1).getTime()
    : period === "year" ? new Date(now.getFullYear(), 0, 1).getTime() : 0;
  const inPeriod = quotes.filter((q: any) => (q.createdAt || 0) >= from);

  // company tiles for the period
  const sent = inPeriod.length;
  const won = inPeriod.filter((q: any) => TEAM_WON.includes(q.status));
  const open = inPeriod.filter((q: any) => TEAM_OPEN.includes(q.status));
  const acceptance = sent ? Math.round((won.length / sent) * 100) : 0;
  const pipeline$ = open.reduce((a: number, q: any) => a + (q.total || 0), 0);
  const revenue$ = won.reduce((a: number, q: any) => a + (q.total || 0), 0);
  const deposits$ = quotes.reduce((a: number, q: any) => a + teamCollected(q, from), 0);
  const paidRate = won.length ? Math.round((won.filter((q: any) => q.status === "paid").length / won.length) * 100) : 0;
  // speed metric: estimate saved → quote sent (the follow-up sequence handles the chase after that)
  const t2q = React.useMemo(() => {
    const ds: number[] = [];
    quotes.forEach((q: any) => {
      if ((q.createdAt || 0) < from) return;
      const proj = (store.projects || []).find((p: any) => p.id === q.projectId);
      const sent = (q.sends && q.sends[0] && q.sends[0].at) || q.lastSentAt || q.createdAt;
      if (proj && proj.createdAt && sent && sent > proj.createdAt && sent - proj.createdAt < 30 * 86400000) ds.push(sent - proj.createdAt);
    });
    return ds.length ? ds.reduce((a, b) => a + b, 0) / ds.length : null;
  }, [quotes, from, store.projects]);
  const t2qFmt = t2q == null ? "—" : t2q < 3600000 ? Math.max(1, Math.round(t2q / 60000)) + " min" : t2q < 48 * 3600000 ? (t2q / 3600000).toFixed(1) + " hr" : (t2q / 86400000).toFixed(1) + " d";

  // per-rep stats: roster ∪ reps already credited on quotes
  const rows = React.useMemo(() => {
    const by: any = {};
    const rowFor = (name: string) => {
      const k = repKey(name) || "unassigned";
      if (!by[k]) {
        const r = roster.find((x: any) => repKey(x.name) === k || (x.email && repKey(x.email) === k));
        by[k] = { name: k === "unassigned" ? "Unassigned" : name, roster: r || null, sent: 0, won: 0, pipeline: 0, revenue: 0, collected: 0 };
      }
      return by[k];
    };
    roster.forEach((r: any) => rowFor(r.name || r.email));
    inPeriod.forEach((q: any) => {
      const r = rowFor((q.rep || "").trim() || "Unassigned");
      r.sent++;
      if (TEAM_WON.includes(q.status)) { r.won++; r.revenue += q.total || 0; }
      else if (TEAM_OPEN.includes(q.status)) r.pipeline += q.total || 0;
    });
    quotes.forEach((q: any) => { const amt = teamCollected(q, from); if (amt > 0) rowFor((q.rep || "").trim() || "Unassigned").collected += amt; });
    return (Object.values(by) as any[])
      .sort((a, b) => (b.revenue - a.revenue) || (b.pipeline - a.pipeline) || (b.sent - a.sent))
      .sort((a, b) => (a.name === "Unassigned" ? 1 : 0) - (b.name === "Unassigned" ? 1 : 0));
  }, [quotes, roster, from]);

  // reps credited on quotes but not on the roster yet → one-tap add
  const unrostered = React.useMemo(() => {
    const have = new Set(roster.map((r: any) => repKey(r.name)));
    const found = new Set<string>();
    quotes.forEach((q: any) => { const n = (q.rep || "").trim(); if (n && !have.has(repKey(n))) found.add(n); });
    return [...found];
  }, [quotes, roster]);

  const setRoster = (next: any[]) => store.setSettings({ salesReps: next });
  const upd = (id: string, patch: any) => setRoster(roster.map((r: any) => r.id === id ? { ...r, ...patch } : r));
  const addRep = (name: string, email?: string) => {
    if (!name.trim()) return;
    setRoster([...roster, { id: "rep-" + Date.now(), name: name.trim(), email: (email || "").trim(), commissionPct: 5, goalMo: null }]);
  };
  const [nName, setNName] = React.useState<any>("");
  const [nEmail, setNEmail] = React.useState<any>("");
  const goalFor = (r: any) => r && r.goalMo ? (period === "month" ? +r.goalMo : period === "year" ? +r.goalMo * 12 : null) : null;
  const medal = ["#f5c451", "#c0c8d4", "#d19a6b"];

  return (
    <div className="page fade-up">
      <div className="row between" style={{ marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div><h1 className="display" style={{ fontSize: 28, margin: 0 }}>Sales Team</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>Who's winning, what they're owed, and how the funnel looks — credited by the rep set on each estimate.</p></div>
        <div className="row gap-8">
          {[["month", "This Month"], ["year", "This Year"], ["all", "All Time"]].map(([id, lb]) => (
            <button key={id} className={"filter-chip" + (period === id ? " on" : "")} onClick={() => setPeriod(id)}>{lb}</button>
          ))}
        </div>
      </div>

      {/* company tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 18 }}>
        {[["Sent opportunities", sent, `${period === "all" ? "all time" : period === "year" ? "this year" : "this month"}`, null],
          ["Acceptance rate", acceptance + "%", `${won.length} of ${sent} sent`, acceptance >= 35 ? "var(--green)" : "var(--amber)"],
          ["Open pipeline", BZ_money0(pipeline$), `${open.length} open opportunities`, "var(--violet)"],
          ["Revenue won", BZ_money0(revenue$), `${won.length} won`, "var(--teal)"],
          ["Deposits collected", BZ_money0(deposits$), "payments in period", "var(--green)"],
          ["Payment rate", paidRate + "%", "paid of accepted", paidRate >= 70 ? "var(--green)" : "var(--amber)"],
          ["Speed to quote", t2qFmt, "estimate saved → sent", t2q != null && t2q <= 24 * 3600000 ? "var(--green)" : "var(--amber)"]].map(([lb, v, sub, col]: any) => (
          <div key={lb} className="card pad" style={{ padding: "13px 15px" }}>
            <div className="kicker" style={{ marginBottom: 6 }}>{lb}</div>
            <div className="display tnum" style={{ fontSize: 22, color: col || "var(--fg)" }}>{v}</div>
            <div className="muted" style={{ fontSize: 11.5 }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* rankings */}
      <div className="card pad" style={{ marginBottom: 18 }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 16 }}>Rep rankings</h3>
        <p className="muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>Ranked by revenue won. Commission uses each rep's % of won revenue; pending = % of their open pipeline.</p>
        {rows.length === 0
          ? <EmptyState icon="user" title="No activity yet" sub="Send estimates with a rep set (Customer & Job Info) and rankings build themselves." />
          : <div style={{ overflowX: "auto" }}>
            <table className="rep-src-table">
              <thead><tr><th>#</th><th>Rep</th><th className="num">Sent</th><th className="num">Won</th><th className="num">Close</th><th className="num">Pipeline</th><th className="num">Revenue</th><th className="num">Collected</th><th className="num">Commission</th><th>Goal</th></tr></thead>
              <tbody>
                {rows.map((r: any, i: number) => {
                  const un = r.name === "Unassigned";
                  const close = r.sent ? Math.round((r.won / r.sent) * 100) : 0;
                  const pct = r.roster ? (Number(r.roster.commissionPct) || 0) : null;
                  const goal = goalFor(r.roster);
                  const gp = goal ? Math.min(100, Math.round((r.revenue / goal) * 100)) : null;
                  return (
                    <tr key={r.name}>
                      <td><span style={{ display: "inline-grid", placeItems: "center", width: 22, height: 22, borderRadius: 99, fontSize: 11, fontWeight: 800, color: !un && i < 3 ? "#06121a" : "var(--fg-3)", background: !un && i < 3 ? medal[i] : "var(--surface-3)" }}>{i + 1}</span></td>
                      <td style={{ fontWeight: 700, color: un ? "var(--fg-3)" : "var(--fg)" }}>{r.name}{r.roster && r.roster.email ? <div className="faint" style={{ fontSize: 10.5, fontWeight: 500 }}>{r.roster.email}</div> : null}</td>
                      <td className="num tnum">{r.sent}</td>
                      <td className="num tnum">{r.won}</td>
                      <td className="num"><span className="tnum" style={{ fontWeight: 700, color: close >= 40 ? "var(--green)" : close >= 25 ? "var(--amber)" : "var(--red)" }}>{close}%</span></td>
                      <td className="num tnum" style={{ color: "var(--fg-2)" }}>{r.pipeline > 0 ? BZ_money0(r.pipeline) : "—"}</td>
                      <td className="num tnum" style={{ fontWeight: 700, color: r.revenue > 0 ? "var(--teal)" : "var(--fg-3)" }}>{r.revenue > 0 ? BZ_money0(r.revenue) : "—"}</td>
                      <td className="num tnum" style={{ color: r.collected > 0 ? "var(--green)" : "var(--fg-3)" }}>{r.collected > 0 ? BZ_money0(r.collected) : "—"}</td>
                      <td className="num">{pct == null ? <span className="faint">—</span> : (
                        <span className="tnum" style={{ fontWeight: 700, color: "var(--green)" }}>{BZ_money0(r.revenue * pct / 100)}
                          {r.pipeline > 0 && <span className="muted" style={{ fontWeight: 500, fontSize: 11 }}> +{BZ_money0(r.pipeline * pct / 100)} pend.</span>}</span>)}</td>
                      <td style={{ minWidth: 110 }}>{gp == null ? <span className="faint">—</span> : (
                        <div title={`${BZ_money0(r.revenue)} of ${BZ_money0(goal)}`}>
                          <div className="row between" style={{ fontSize: 10.5, marginBottom: 3 }}><span className="muted">{gp}%</span><span className="faint tnum">{BZ_money0(goal)}</span></div>
                          <div style={{ height: 5, borderRadius: 99, background: "var(--surface-3)", overflow: "hidden" }}>
                            <div style={{ width: gp + "%", height: "100%", background: gp >= 100 ? "var(--green)" : "var(--teal)" }} />
                          </div>
                        </div>)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>}
      </div>

      <CommissionsCard store={store} />

      {/* roster */}
      <div className="card pad">
        <h3 style={{ margin: "0 0 4px", fontSize: 16 }}>Roster · commission &amp; goals</h3>
        <p className="muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>Reps added here show up in the Estimator's rep picker. Workspace logins are managed in <a style={{ color: "var(--teal)", cursor: "pointer" }} onClick={() => go("account", { tab: "team" })}>Account → Team</a>.</p>
        {/* recovery: reps credited on quotes but missing from the roster — the
            quotes are the durable record, so the roster rebuilds in one tap
            (e.g. after a stale pre-update tab overwrote cloud settings). */}
        {roster.length === 0 && unrostered.length > 0 && (
          <div className="pe-callout info" style={{ marginBottom: 12 }}>
            <p className="muted" style={{ margin: "0 0 8px", fontSize: 12.5 }}>
              Found <b style={{ color: "var(--fg)" }}>{unrostered.length} rep{unrostered.length === 1 ? "" : "s"}</b> credited on your quotes but not on the roster. Their sales history is safe — restore them here. (Tip: close FenceWorks tabs still running an older version; a stale tab can overwrite newer settings.)
            </p>
            <button className="btn btn-primary btn-sm" onClick={() => setRoster(unrostered.map((n, i) => ({ id: "rep-" + Date.now() + "-" + i, name: n, email: "", commissionPct: 5, goalMo: null })))}>
              <Icon name="undo" size={13} />Restore {unrostered.length === 1 ? unrostered[0] : "all " + unrostered.length + " reps"}
            </button>
          </div>
        )}
        {roster.length > 0 && unrostered.length > 0 && (
          <div className="row gap-8" style={{ flexWrap: "wrap", marginBottom: 12 }}>
            <span className="faint" style={{ fontSize: 11.5, alignSelf: "center" }}>Seen on quotes:</span>
            {unrostered.map(n => <button key={n} className="filter-chip" onClick={() => addRep(n)} title="Add to the roster">+ {n}</button>)}
          </div>
        )}
        <div style={{ display: "grid", gap: 8 }}>
          {roster.map((r: any) => (
            <div key={r.id} className="ready-row" style={{ flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 160px", minWidth: 140 }}>
                <input className="input" value={r.name || ""} placeholder="Rep name" onChange={e => upd(r.id, { name: e.target.value })} />
              </div>
              <div style={{ flex: "1 1 180px", minWidth: 150 }}>
                <input className="input" value={r.email || ""} placeholder="email (optional)" onChange={e => upd(r.id, { email: e.target.value })} />
              </div>
              <NumInput value={r.commissionPct != null ? r.commissionPct : 5} step={0.5} width={48} suffix="% comm." onCommit={(v: any) => upd(r.id, { commissionPct: v == null ? 0 : Math.min(50, v) })} />
              <NumInput value={r.goalMo} step={1000} width={72} suffix="/mo goal" placeholder="—" onCommit={(v: any) => upd(r.id, { goalMo: v })} />
              <button className="pe-del" title="Remove rep (their quote history stays)" onClick={() => setRoster(roster.filter((x: any) => x.id !== r.id))}><Icon name="trash" size={13} /></button>
            </div>
          ))}
          <div className="ready-row" style={{ flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 160px", minWidth: 140 }}><input className="input" value={nName} placeholder="New rep name" onChange={e => setNName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { addRep(nName, nEmail); setNName(""); setNEmail(""); } }} /></div>
            <div style={{ flex: "1 1 180px", minWidth: 150 }}><input className="input" value={nEmail} placeholder="email (optional)" onChange={e => setNEmail(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { addRep(nName, nEmail); setNName(""); setNEmail(""); } }} /></div>
            <button className="btn btn-primary btn-sm" disabled={!nName.trim()} onClick={() => { addRep(nName, nEmail); setNName(""); setNEmail(""); }}><Icon name="plus" size={13} />Add rep</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- commission payouts: owed vs paid per rep per month --------------------
   Commission accrues when a quote is WON (wonAt, stamped on the signed/paid
   transition; older quotes fall back to lastSentAt/createdAt). Payouts are
   recorded rows in settings.commissionPayouts; balance = owed − paid. */
const wonAtOf = (q: any) => q.wonAt || q.lastSentAt || q.createdAt || 0;
const monthKey = (t: number) => { const d = new Date(t); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0"); };
const monthLabel = (mk: string) => { const [y, m] = mk.split("-"); return new Date(+y, +m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" }); };

/* ---- how a commission was actually paid -----------------------------------
   "Mark paid" used to stamp the full balance with nothing but a date — no
   method, no check number, no way to record a partial. Payroll then had a
   number it couldn't reconcile against a bank statement. Recording a payout
   now asks the three things that make it reconcilable: how much, how, and the
   reference that proves it. */
const PAY_METHODS = [
  { id: "check", label: "Check", ref: "Check #", ph: "1042", need: true },
  { id: "deposit", label: "Direct deposit", ref: "Trace / confirmation #", ph: "ACH trace or bank confirmation", need: false },
  { id: "payroll", label: "On their paycheck", ref: "Payroll run", ph: "e.g. 09/30 payroll", need: false },
  { id: "cash", label: "Cash", ref: "Note", ph: "who handed it over", need: false },
  { id: "other", label: "Other", ref: "Reference", ph: "however you tracked it", need: false },
];
const methodOf = (id: any) => PAY_METHODS.find(m => m.id === id) || PAY_METHODS[0];
const todayISO = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };

function PayoutDialog({ row, mk, onSave, onClose }: any) {
  const [amount, setAmount] = React.useState<any>(String(Math.round(row.balance * 100) / 100));
  const [method, setMethod] = React.useState<any>("check");
  const [reference, setReference] = React.useState<any>("");
  const [paidOn, setPaidOn] = React.useState<any>(todayISO());
  const [note, setNote] = React.useState<any>("");
  const [err, setErr] = React.useState<any>("");
  const m = methodOf(method);
  const amt = Math.round((parseFloat(amount) || 0) * 100) / 100;
  const over = amt > row.balance + 0.005;
  React.useEffect(() => {
    const f = (e: any) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", f); return () => window.removeEventListener("keydown", f);
  }, [onClose]);

  function submit() {
    if (!(amt > 0)) { setErr("Enter the amount you paid."); return; }
    // the check number is the whole point of recording a check
    if (m.need && !reference.trim()) { setErr("Add the check number so this can be reconciled later."); return; }
    onSave({
      amount: amt, method, reference: reference.trim(),
      paidOn: paidOn || todayISO(), note: note.trim(),
    });
  }
  return ReactDOM.createPortal(
    <div className="cs-overlay" data-dialog onClick={onClose}>
      <div className="payout-modal" role="dialog" aria-label="Record commission payout" onClick={(e: any) => e.stopPropagation()}>
        <div className="row between" style={{ marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Record payout</h3>
          <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>
        <p className="muted" style={{ fontSize: 12.5, margin: "0 0 14px" }}>
          <b style={{ color: "var(--fg-2)" }}>{row.r.name}</b> · {monthLabel(mk)} · balance {BZ_money0(row.balance)}
        </p>

        <label className="field-label" htmlFor="po-amt">Amount paid</label>
        <div className="row gap-8" style={{ alignItems: "center", marginBottom: 12 }}>
          <div className="pe-money-input" style={{ maxWidth: 160 }}><span>$</span>
            <input id="po-amt" className="tnum" type="number" inputMode="decimal" min="0" step="0.01" value={amount}
              onChange={e => { setAmount(e.target.value); setErr(""); }} />
          </div>
          {amt > 0 && amt < row.balance - 0.005 &&
            <span className="chip" style={{ fontSize: 11, color: "var(--amber)", borderColor: "var(--amber)" }}>Partial — {BZ_money0(row.balance - amt)} still owed</span>}
          {over && <span className="chip" style={{ fontSize: 11, color: "var(--amber)", borderColor: "var(--amber)" }}>More than the balance</span>}
        </div>

        <label className="field-label">How was it paid?</label>
        <div className="payout-methods" role="group" aria-label="Payment method">
          {PAY_METHODS.map(o => (
            <button key={o.id} type="button" className={"payout-method" + (method === o.id ? " on" : "")}
              aria-pressed={method === o.id} onClick={() => { setMethod(o.id); setErr(""); }}>{o.label}</button>
          ))}
        </div>

        <div className="pe-row2" style={{ marginTop: 12 }}>
          <div className="pe-field">
            <label className="field-label" htmlFor="po-ref">{m.ref}{!m.need && <span className="faint" style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}> — optional</span>}</label>
            <input id="po-ref" className="input" placeholder={m.ph} value={reference} onChange={e => { setReference(e.target.value); setErr(""); }} />
          </div>
          <div className="pe-field">
            <label className="field-label" htmlFor="po-date">Date paid</label>
            <input id="po-date" className="input" type="date" value={paidOn} onChange={e => setPaidOn(e.target.value)} />
          </div>
        </div>

        <div className="pe-field" style={{ marginTop: 10 }}>
          <label className="field-label" htmlFor="po-note">Note <span className="faint" style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— optional</span></label>
          <input id="po-note" className="input" placeholder="anything payroll should see on the statement" value={note} onChange={e => setNote(e.target.value)} />
        </div>

        {err && <p style={{ color: "var(--red)", fontSize: 12.5, margin: "10px 0 0" }}>{err}</p>}
        <div className="row gap-8" style={{ marginTop: 16, flexWrap: "wrap" }}>
          <button className="btn btn-primary" onClick={submit}><Icon name="check" size={14} />Record {amt > 0 ? BZ_money0(amt) : "payout"}</button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* one line describing a recorded payout — "Check #1042", "Direct deposit · ACH…" */
function payoutDetail(p: any) {
  if (!p.method) return "";                       // recorded before methods existed
  const m = methodOf(p.method);
  if (p.method === "check") return p.reference ? `Check #${p.reference}` : "Check";
  return m.label + (p.reference ? " · " + p.reference : "");
}

function CommissionsCard({ store }: any) {
  const settings = store.settings || {};
  const roster = (settings.salesReps || []).filter((r: any) => r.name);
  const payouts = settings.commissionPayouts || [];
  const quotes = store.quotes || [];
  const months = React.useMemo(() => {   // this month + the 5 before it
    const out: string[] = []; const d = new Date();
    for (let i = 0; i < 6; i++) out.push(monthKey(new Date(d.getFullYear(), d.getMonth() - i, 1).getTime()));
    return out;
  }, []);
  const [mk, setMk] = React.useState<any>(months[0]);
  const [statement, setStatement] = React.useState<any>(null);   // rep row for the printable statement

  const wonIn = (repName: string) => quotes.filter((q: any) => TEAM_WON.includes(q.status)
    && repKey((q.rep || "").trim()) === repKey(repName) && monthKey(wonAtOf(q)) === mk);
  const rows = roster.map((r: any) => {
    const won = wonIn(r.name);
    const rev = won.reduce((a: number, q: any) => a + (q.total || 0), 0);
    const pct = Number(r.commissionPct) || 0;
    const owed = rev * pct / 100;
    const paid = payouts.filter((p: any) => repKey(p.rep) === repKey(r.name) && p.forMonth === mk)
      .reduce((a: number, p: any) => a + (Number(p.amount) || 0), 0);
    return { r, won, rev, pct, owed, paid, balance: Math.max(0, owed - paid) };
  }).filter((x: any) => x.pct > 0 || x.owed > 0 || x.paid > 0);

  const [recording, setRecording] = React.useState<any>(null);   // rep row awaiting its payout details
  const record = (row: any, d: any) => {
    const entry = {
      id: "cp-" + Date.now(), rep: row.r.name, forMonth: mk, at: Date.now(),
      amount: d.amount, method: d.method, reference: d.reference, paidOn: d.paidOn, note: d.note,
    };
    store.setSettings({ commissionPayouts: [...payouts, entry] });
    const how = payoutDetail(entry);
    store.addActivity(`Commission payout ${BZ_money0(entry.amount)} → ${row.r.name} (${monthLabel(mk)})${how ? " · " + how : ""}`);
    setRecording(null);
  };
  const removePayout = (id: string) => store.setSettings({ commissionPayouts: payouts.filter((p: any) => p.id !== id) });
  const recent = payouts.slice().sort((a: any, b: any) => (b.at || 0) - (a.at || 0)).slice(0, 8);

  return (
    <div className="card pad" style={{ marginBottom: 18 }}>
      <div className="row between" style={{ marginBottom: 4, flexWrap: "wrap", gap: 10 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Commission payouts</h3>
        <select className="select" style={{ width: 170 }} value={mk} onChange={e => setMk(e.target.value)}>
          {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>
      </div>
      <p className="muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>Owed = each rep's % of revenue <b style={{ color: "var(--fg-2)" }}>won</b> in the month (by sign date). Record a payout when you cut the check; the statement prints for payroll.</p>
      {rows.length === 0
        ? <EmptyState icon="dollar" title="No commission activity" sub="Set a commission % on the roster below — owed builds from won quotes automatically." />
        : <div style={{ overflowX: "auto" }}>
          <table className="rep-src-table">
            <thead><tr><th>Rep</th><th className="num">Won jobs</th><th className="num">Won revenue</th><th className="num">%</th><th className="num">Owed</th><th className="num">Paid</th><th className="num">Balance</th><th></th></tr></thead>
            <tbody>
              {rows.map((row: any) => (
                <tr key={row.r.id}>
                  <td style={{ fontWeight: 700 }}>{row.r.name}</td>
                  <td className="num tnum">{row.won.length}</td>
                  <td className="num tnum">{BZ_money0(row.rev)}</td>
                  <td className="num tnum">{row.pct}%</td>
                  <td className="num tnum" style={{ fontWeight: 700 }}>{BZ_money0(row.owed)}</td>
                  <td className="num tnum" style={{ color: row.paid > 0 ? "var(--green)" : "var(--fg-3)" }}>{row.paid > 0 ? BZ_money0(row.paid) : "—"}</td>
                  <td className="num tnum" style={{ fontWeight: 800, color: row.balance > 0.5 ? "var(--amber)" : "var(--green)" }}>{row.balance > 0.5 ? BZ_money0(row.balance) : "settled ✓"}</td>
                  <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                    {row.balance > 0.5 && <button className="btn btn-outline btn-sm" onClick={() => setRecording(row)} title="Record how this commission was paid">Record payout</button>}
                    <button className="btn btn-ghost btn-sm" style={{ marginLeft: 6 }} onClick={() => setStatement(row)} title="Printable commission statement"><Icon name="printer" size={12} />Statement</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      {recent.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="kicker" style={{ marginBottom: 6 }}>Recent payouts</div>
          <div style={{ display: "grid", gap: 5 }}>
            {recent.map((p: any) => {
              const how = payoutDetail(p);
              const paidOn = p.paidOn ? new Date(p.paidOn + "T12:00:00").toLocaleDateString() : new Date(p.at).toLocaleDateString();
              return (
              <div key={p.id} className="payout-row">
                <Icon name="dollar" size={12} style={{ color: "var(--green)", flexShrink: 0, marginTop: 3 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5 }}><b>{BZ_money0(p.amount)}</b> → {p.rep} <span className="muted">· {monthLabel(p.forMonth)}</span></div>
                  <div className="muted" style={{ fontSize: 11.5 }}>
                    {how ? <><b style={{ color: "var(--fg-2)" }}>{how}</b> · </> : null}paid {paidOn}
                    {p.note ? " · " + p.note : ""}
                  </div>
                </div>
                <button className="pe-del" title="Delete payout record" onClick={() => removePayout(p.id)}><Icon name="x" size={11} /></button>
              </div>
            );})}
          </div>
        </div>
      )}
      {recording && <PayoutDialog row={recording} mk={mk} onSave={(d: any) => record(recording, d)} onClose={() => setRecording(null)} />}
      {statement && <CommissionStatement row={statement} mk={mk} store={store} payouts={payouts} onClose={() => setStatement(null)} />}
    </div>
  );
}

/* printable per-rep commission statement (same print overlay as the crew sheet) */
function CommissionStatement({ row, mk, store, payouts, onClose }: any) {
  const company = (store.company && store.company.name) || (store.settings && store.settings.companyName) || "FenceWorks";
  // what was already paid against this month, and how — the statement is what
  // payroll reconciles against a bank statement, so the check numbers belong on it
  const paidRows = (payouts || []).filter((p: any) => repKey(p.rep) === repKey(row.r.name) && p.forMonth === mk)
    .sort((a: any, b: any) => (a.at || 0) - (b.at || 0));
  React.useEffect(() => {
    const f = (e: any) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", f); return () => window.removeEventListener("keydown", f);
  }, [onClose]);
  const mono: any = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" };
  return ReactDOM.createPortal(
    <div className="cs-overlay" data-dialog onClick={onClose}>
      <div className="cs-modal" onClick={(e: any) => e.stopPropagation()}>
        <div className="cs-bar no-print">
          <span className="row gap-8" style={{ fontSize: 13, fontWeight: 700 }}><Icon name="printer" size={14} className="muted" />Commission statement</span>
          <div className="row gap-8">
            <button className="btn btn-primary btn-sm" onClick={() => window.print()}><Icon name="printer" size={14} />Print / PDF</button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="x" size={14} /></button>
          </div>
        </div>
        <div className="cs-scroll" style={{ background: "#3a4353" }}>
          <div style={{ background: "#fff", color: "#1a2430", maxWidth: 760, margin: "0 auto", padding: "26px 30px", ...mono }}>
            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "2px solid #1a2430", paddingBottom: 10, marginBottom: 14 }}>
              <div><div style={{ fontWeight: 800, fontSize: 17 }}>{company}</div><div style={{ fontSize: 11, color: "#8d99a8" }}>COMMISSION STATEMENT</div></div>
              <div style={{ textAlign: "right", fontSize: 12 }}><div style={{ fontWeight: 800 }}>{row.r.name}</div><div>{monthLabel(mk)}</div><div style={{ color: "#8d99a8" }}>{row.pct}% of won revenue</div></div>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr>{["JOB", "ADDRESS", "SOLD", "COMMISSION"].map(hd => <th key={hd} style={{ textAlign: hd === "JOB" || hd === "ADDRESS" ? "left" : "right", padding: "6px 6px", borderBottom: "1px solid #1a2430", fontSize: 9, letterSpacing: ".08em", color: "#8d99a8" }}>{hd}</th>)}</tr></thead>
              <tbody>
                {row.won.map((q: any) => (
                  <tr key={q.id}>
                    <td style={{ padding: "6px 6px", borderBottom: "1px solid #e3ebf3", fontWeight: 700 }}>{q.customer || "Job"}</td>
                    <td style={{ padding: "6px 6px", borderBottom: "1px solid #e3ebf3", color: "#5b6673" }}>{q.address || "—"}</td>
                    <td style={{ padding: "6px 6px", borderBottom: "1px solid #e3ebf3", textAlign: "right" }}>{BZ_money0(q.total || 0)}</td>
                    <td style={{ padding: "6px 6px", borderBottom: "1px solid #e3ebf3", textAlign: "right", fontWeight: 700 }}>{BZ_money0((q.total || 0) * row.pct / 100)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: "grid", justifyContent: "end", gap: 4, marginTop: 12, fontSize: 12.5 }}>
              <div style={{ display: "flex", gap: 24, justifyContent: "space-between" }}><span style={{ color: "#8d99a8" }}>Commission earned</span><b>{BZ_money0(row.owed)}</b></div>
              <div style={{ display: "flex", gap: 24, justifyContent: "space-between" }}><span style={{ color: "#8d99a8" }}>Paid to date ({monthLabel(mk)})</span><b>{BZ_money0(row.paid)}</b></div>
              <div style={{ display: "flex", gap: 24, justifyContent: "space-between", borderTop: "2px solid #1a2430", paddingTop: 6, fontSize: 15 }}><span>Balance due</span><b>{BZ_money0(row.balance)}</b></div>
            </div>
            {paidRows.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 9, letterSpacing: ".08em", color: "#8d99a8", marginBottom: 6 }}>PAYMENTS RECORDED</div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead><tr>{["DATE", "METHOD", "REFERENCE", "AMOUNT"].map(hd => (
                    <th key={hd} style={{ textAlign: hd === "AMOUNT" ? "right" : "left", padding: "6px 6px", borderBottom: "1px solid #1a2430", fontSize: 9, letterSpacing: ".08em", color: "#8d99a8" }}>{hd}</th>
                  ))}</tr></thead>
                  <tbody>
                    {paidRows.map((p: any) => (
                      <tr key={p.id}>
                        <td style={{ padding: "6px 6px", borderBottom: "1px solid #e3ebf3" }}>{p.paidOn ? new Date(p.paidOn + "T12:00:00").toLocaleDateString() : new Date(p.at).toLocaleDateString()}</td>
                        <td style={{ padding: "6px 6px", borderBottom: "1px solid #e3ebf3" }}>{p.method ? methodOf(p.method).label : "—"}</td>
                        <td style={{ padding: "6px 6px", borderBottom: "1px solid #e3ebf3", color: "#5b6673" }}>{p.reference || "—"}{p.note ? " · " + p.note : ""}</td>
                        <td style={{ padding: "6px 6px", borderBottom: "1px solid #e3ebf3", textAlign: "right", fontWeight: 700 }}>{BZ_money0(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p style={{ fontSize: 9, color: "#8d99a8", marginTop: 18 }}>Commission accrues on the signed date of each job. Generated from FenceWorks · {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* the few layout rules inline styles can't express (grid breakpoints, button states) */
const BZ_CSS = `
.bz-season { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 6px; }
.bz-season-cell { border: 1px solid var(--line); border-radius: 10px; padding: 7px 8px; background: var(--surface); min-width: 0; }
.bz-step { flex: 1; height: 26px; border-radius: 7px; border: 1px solid var(--line-2); background: var(--surface-2); color: var(--fg-2); font-weight: 800; font-size: 14px; line-height: 1; cursor: pointer; }
.bz-step:hover:not(:disabled) { color: var(--teal); border-color: var(--teal-tint-2); }
.bz-step:disabled { opacity: .4; cursor: default; }
.bz-radar-row:hover td { background: var(--surface-2); }
.bz-rl { border: 1px solid var(--line); border-radius: 10px; padding: 9px 11px; cursor: pointer; }
.bz-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 12px; }
.bz-be { text-align: right; }
@media (max-width: 560px) {
  .bz-season { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .bz-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .bz-tiles > .card:first-child { grid-column: 1 / -1; }
  .bz-be { text-align: left; }
  .bz-grow { flex-basis: 100% !important; }
}
`;

function BusinessPage({ store, go }: any) {
  return (
    <div className="page fade-up">
      <style>{BZ_CSS}</style>
      <div style={{ marginBottom: 16 }}>
        <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Budget &amp; Cash</h1>
        <p className="muted" style={{ margin: "4px 0 0" }}>Plan the year's numbers, then watch weekly cash before compression hits — all from the jobs already in FenceWorks.</p>
      </div>
      <BudgetSection store={store} go={go} />
      <CashSection store={store} go={go} />
    </div>
  );
}

Object.assign(window, { BusinessPage, SalesTeamPage });

export { BusinessPage, SalesTeamPage };
